import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertFrequency,
  TimeWindow,
  AlertCondition,
  AlertPriority,
  AlertCategory,
} from "../types/alerts";
import {
  ALERT_FREQUENCY_OPTIONS,
  ALERT_TIME_WINDOW_OPTIONS,
  ALERT_CONDITION_OPTIONS,
  ALERT_PRIORITY_OPTIONS,
  ALERT_TEMPLATES,
  PRIORITY_DESCRIPTIONS,
  formatCategoryLabel,
  isPredefinedCategory,
  PREDEFINED_CATEGORIES,
} from "../constants/alerts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Eye } from "lucide-react";
import { ChannelSelection } from "@/components/alerts/ChannelSelection";
import { useChannels } from "@/hooks/useChannels";

export function CreateAlertPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<AlertCategory>("CUSTOM");
  const [customCategoryName, setCustomCategoryName] = useState("");
  const [priority, setPriority] = useState<AlertPriority>("P2");
  const [frequency, setFrequency] = useState<AlertFrequency>("every minute");
  const [timeWindow, setTimeWindow] = useState<TimeWindow>("the last 5 minutes");
  const [condition, setCondition] = useState<AlertCondition>("the query has any results");
  const [conditionValue, setConditionValue] = useState<number>(0);
  const [showPreview, setShowPreview] = useState(false);
  const { channels, selectedChannels, updateChannels, setSelectedChannels } = useChannels();

  const handleCategoryChange = (newCategory: AlertCategory) => {
    setCategory(newCategory);
    if (
      newCategory !== "CUSTOM" &&
      isPredefinedCategory(newCategory) &&
      ALERT_TEMPLATES[newCategory]
    ) {
      const template = ALERT_TEMPLATES[newCategory];
      setQuery(template.query);
      setPriority(template.priority);
      // Use functional update to avoid stale closure issues
      setDescription((prevDescription) =>
        template.description && !prevDescription ? template.description : prevDescription
      );
      setCustomCategoryName(""); // Clear custom name when switching to predefined
    } else if (newCategory === "CUSTOM") {
      setCustomCategoryName(""); // Reset custom name
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
    // In a real app, this would call an API to preview query results
    setTimeout(() => setShowPreview(false), 2000);
  };

  const handleSubmit = () => {
    // In a real app, this would save to backend
    const finalCategory =
      category === "CUSTOM" && customCategoryName.trim()
        ? customCategoryName.trim()
        : isPredefinedCategory(category)
          ? category
          : category === "CUSTOM"
            ? "CUSTOM"
            : category;

    console.log({
      name,
      description,
      query,
      category: finalCategory,
      customCategoryName:
        category === "CUSTOM" && customCategoryName.trim() ? customCategoryName.trim() : undefined,
      priority,
      frequency,
      timeWindow,
      condition,
      conditionValue: condition === "the query has any results" ? undefined : conditionValue,
      channels: selectedChannels,
    });
    navigate("/alerts");
  };

  const needsConditionValue = condition !== "the query has any results";

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/alerts")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Create Alert</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Set up custom alerts based on natural language queries for your AI stack
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Alert Name</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="alert-name">Alert name</Label>
            <Input
              id="alert-name"
              placeholder="e.g., Critical PII Detection Alert"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Category & Priority</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Alert Category</Label>
              <Select
                value={category}
                onValueChange={(value) => handleCategoryChange(value as AlertCategory)}
              >
                <SelectTrigger id="category">
                  <SelectValue>{formatCategoryLabel(category, customCategoryName)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {PREDEFINED_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {formatCategoryLabel(cat)}
                    </SelectItem>
                  ))}
                  <SelectItem value="CUSTOM">Custom Category</SelectItem>
                </SelectContent>
              </Select>

              {category === "CUSTOM" && (
                <div className="space-y-2 mt-2">
                  <Label htmlFor="custom-category-name">Custom Category Name</Label>
                  <Input
                    id="custom-category-name"
                    placeholder="e.g., Compliance Audit, Cost Monitoring"
                    value={customCategoryName}
                    onChange={(e) => setCustomCategoryName(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Create your own category name for this alert
                  </p>
                </div>
              )}

              {isPredefinedCategory(category) && (
                <p className="text-xs text-muted-foreground mt-1">
                  {ALERT_TEMPLATES[category].description}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority Level</Label>
              <Select
                value={priority}
                onValueChange={(value) => setPriority(value as AlertPriority)}
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALERT_PRIORITY_OPTIONS.map((pri) => (
                    <SelectItem key={pri} value={pri}>
                      <div className="flex items-center gap-2">
                        <span>{pri}</span>
                        <span className="text-xs text-muted-foreground">
                          - {PRIORITY_DESCRIPTIONS[pri].label} (SLA:{" "}
                          {PRIORITY_DESCRIPTIONS[pri].sla})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {PRIORITY_DESCRIPTIONS[priority].label} • SLA: {PRIORITY_DESCRIPTIONS[priority].sla}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Query</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Write your alert query in natural language. You will be alerted when this query
              returns one or more results.
            </p>
            <Textarea
              placeholder="e.g., Alert me when there are critical PII detection issues from any AI agent"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="min-h-[120px] font-mono text-sm"
            />
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Examples:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>"Alert me when the error rate for any AI agent exceeds 5%"</li>
                <li>"Notify me when prompt injection scanner detects high severity issues"</li>
                <li>"Alert when token usage for gpt-4 model exceeds 3x baseline"</li>
              </ul>
            </div>
          </div>
          <Button variant="outline" onClick={handlePreview} disabled={!query.trim()}>
            <Eye className="h-4 w-4 mr-2" />
            Preview query results
          </Button>
          {showPreview && (
            <div className="mt-4 p-4 bg-muted rounded-md">
              <p className="text-sm font-medium mb-2">Preview Results (Mock Data)</p>
              <div className="text-xs text-muted-foreground">
                <p>Query would return 3 results:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>PII Detection - CRITICAL - Agent: pii-aware-assistant</li>
                  <li>Prompt Injection - HIGH - Agent: prompt-injection-guard</li>
                  <li>Data Exfiltration - CRITICAL - Agent: data-exfiltration-detector</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alert Parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="frequency">Execute the query</Label>
              <Select
                value={frequency}
                onValueChange={(value) => setFrequency(value as AlertFrequency)}
              >
                <SelectTrigger id="frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALERT_FREQUENCY_OPTIONS.map((freq) => (
                    <SelectItem key={freq} value={freq}>
                      {freq}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="time-window">Include rows from</Label>
              <Select
                value={timeWindow}
                onValueChange={(value) => setTimeWindow(value as TimeWindow)}
              >
                <SelectTrigger id="time-window">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALERT_TIME_WINDOW_OPTIONS.map((window) => (
                    <SelectItem key={window} value={window}>
                      {window}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="condition">Notify me when</Label>
              <Select
                value={condition}
                onValueChange={(value) => setCondition(value as AlertCondition)}
              >
                <SelectTrigger id="condition">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALERT_CONDITION_OPTIONS.map((cond) => (
                    <SelectItem key={cond} value={cond}>
                      {cond}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {needsConditionValue && (
            <div className="mt-4 space-y-2">
              <Label htmlFor="condition-value">
                {condition.includes("rows")
                  ? "Threshold (number of rows)"
                  : condition.includes("error rate")
                    ? "Error Rate Threshold (%)"
                    : condition.includes("standard deviations")
                      ? "Standard Deviations"
                      : "Threshold (percentage)"}
              </Label>
              <Input
                id="condition-value"
                type="number"
                min={0}
                step={condition.includes("standard deviations") ? 0.1 : 1}
                value={conditionValue}
                onChange={(e) => setConditionValue(Number(e.target.value))}
                placeholder={
                  condition.includes("rows")
                    ? "10"
                    : condition.includes("error rate")
                      ? "5"
                      : condition.includes("standard deviations")
                        ? "2"
                        : "300"
                }
              />
              {condition.includes("standard deviations") && (
                <p className="text-xs text-muted-foreground">
                  Number of standard deviations from baseline (typically 2-3)
                </p>
              )}
            </div>
          )}

          <p className="text-sm text-muted-foreground mt-4">
            A notification will be sent to your webhooks when{" "}
            <strong>the query returns one or more rows.</strong>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              This text will be included in notifications. It&apos;s a good place for related links,
              instructions, etc.
            </p>
            <Textarea
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
        </CardContent>
      </Card>

      <ChannelSelection
        channels={channels}
        selectedChannels={selectedChannels}
        onChannelsChange={updateChannels}
        onSelectionChange={setSelectedChannels}
      />

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/alerts")}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name.trim() || !query.trim() || selectedChannels.length === 0}
        >
          Create Alert
        </Button>
      </div>
    </div>
  );
}
